import React, { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

const SOCKET_URL = (() => {
  const base = process.env.REACT_APP_API_URL || '';
  return base.replace(/\/api$/, '') || window.location.origin;
})();

export default function PortalMessagingPanel({ open, onClose, lang = 'en' }) {
  const socketRef = useRef(null);
  const [channels, setChannels] = useState([]);
  const [activeChannelId, setActiveChannelId] = useState(null);
  const [messages, setMessages] = useState({});
  const [typingUsers, setTypingUsers] = useState({});
  const [draft, setDraft] = useState('');
  const [connected, setConnected] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimer = useRef(null);

  const API = process.env.REACT_APP_API_URL || '';
  const auth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('portalToken')}` } });

  const fetchChannels = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/portal/messaging/channels`, auth());
      if (!res.ok) return;
      const data = await res.json();
      setChannels(data);
      if (!activeChannelId && data.length > 0) setActiveChannelId(String(data[0].id));
    } catch { /* ignore */ }
  }, [API, activeChannelId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMessages = useCallback(async (channelId) => {
    try {
      const res = await fetch(`${API}/api/portal/messaging/channels/${channelId}/messages`, auth());
      if (!res.ok) return;
      const data = await res.json();
      setMessages(prev => ({ ...prev, [channelId]: data }));
    } catch { /* ignore */ }
  }, [API]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const token = sessionStorage.getItem('portalToken');
    if (!token) return;

    const socket = io(`${SOCKET_URL}/portal-chat`, {
      path: '/socket.io',
      auth: { token },
      // Polling first, then upgrade — see MessagingContext.js for why 'websocket' first
      // is unsafe on restrictive networks that silently drop the WS upgrade.
      transports: ['polling', 'websocket'],
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      fetchChannels();
    });
    socket.on('disconnect', () => setConnected(false));

    socket.on('message:new', (msg) => {
      setMessages(prev => {
        const existing = prev[msg.channelId] || [];
        if (existing.find(m => m.id === msg.id)) return prev;
        return { ...prev, [msg.channelId]: [...existing, msg] };
      });
    });

    socket.on('user:typing', ({ channelId, userName, typing }) => {
      setTypingUsers(prev => {
        const list = prev[channelId] || [];
        if (typing) return { ...prev, [channelId]: list.includes(userName) ? list : [...list, userName] };
        return { ...prev, [channelId]: list.filter(n => n !== userName) };
      });
    });

    return () => { socket.disconnect(); socketRef.current = null; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open && activeChannelId) {
      fetchMessages(activeChannelId);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [activeChannelId, open, fetchMessages]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [activeChannelId, open]);

  const activeMessages = activeChannelId ? (messages[activeChannelId] || []) : [];
  const activeChannel = channels.find(c => String(c.id) === String(activeChannelId));
  const typing = activeChannelId ? (typingUsers[activeChannelId] || []) : [];

  const handleSend = () => {
    if (!draft.trim() || !activeChannelId) return;
    socketRef.current?.emit('message:send', { channelId: parseInt(activeChannelId, 10), body: draft.trim() });
    setDraft('');
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const notifyTyping = () => {
    socketRef.current?.emit('typing:start', { channelId: parseInt(activeChannelId, 10) });
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => {
      socketRef.current?.emit('typing:stop', { channelId: parseInt(activeChannelId, 10) });
    }, 2000);
  };

  const fmtTime = (ts) => new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1049,
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.25s ease',
        }}
      />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 420, maxWidth: '95vw',
        background: '#fff', display: 'flex', flexDirection: 'column',
        zIndex: 1050, boxShadow: '-4px 0 20px rgba(0,0,0,0.18)',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: open ? 'auto' : 'none',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: '1px solid #dee2e6', flexShrink: 0 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>
            {activeChannel ? `# ${activeChannel.name}` : (lang === 'es' ? 'Mensajes' : 'Messages')}
          </span>
          {!connected && <span style={{ fontSize: 11, color: '#dc3545' }}>●</span>}
          {connected && <span style={{ fontSize: 11, color: '#198754' }}>●</span>}
          <button className="btn-close" onClick={onClose} style={{ marginLeft: 'auto' }} />
        </div>

        {/* Channel tabs */}
        {channels.length > 1 && (
          <div style={{ display: 'flex', gap: 2, padding: '6px 12px', borderBottom: '1px solid #dee2e6', flexShrink: 0, overflowX: 'auto' }}>
            {channels.map(ch => (
              <button
                key={ch.id}
                onClick={() => setActiveChannelId(String(ch.id))}
                style={{
                  border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 13, flexShrink: 0,
                  background: String(ch.id) === String(activeChannelId) ? '#0d6efd' : '#f0f0f0',
                  color: String(ch.id) === String(activeChannelId) ? '#fff' : '#333',
                  fontWeight: String(ch.id) === String(activeChannelId) ? 600 : 400,
                }}
              >
                # {ch.name}
              </button>
            ))}
          </div>
        )}

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
          {activeMessages.length === 0 && (
            <div style={{ textAlign: 'center', color: '#999', marginTop: 24, fontSize: 14 }}>
              {lang === 'es' ? 'Sin mensajes aún' : 'No messages yet'}
            </div>
          )}
          {activeMessages.map(msg => (
            <div key={msg.id} style={{ marginBottom: 10 }}>
              {msg.type === 'system' ? (
                <div style={{ textAlign: 'center', fontSize: 12, color: '#6c757d', fontStyle: 'italic' }}>{msg.body}</div>
              ) : (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                    background: '#0d6efd', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
                  }}>
                    {(msg.authorName || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{msg.authorName}</span>
                      <span style={{ fontSize: 11, color: '#999' }}>{fmtTime(msg.createdAt)}</span>
                    </div>
                    <div style={{ fontSize: 14, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{msg.body}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
          {typing.length > 0 && (
            <div style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}>
              {typing.join(', ')} {typing.length === 1 ? 'is' : 'are'} typing…
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div style={{ padding: '10px 14px', borderTop: '1px solid #dee2e6', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              ref={inputRef}
              className="form-control"
              placeholder={lang === 'es' ? 'Escribe un mensaje…' : 'Type a message…'}
              value={draft}
              onChange={e => { setDraft(e.target.value); notifyTyping(); }}
              onKeyDown={handleKey}
              maxLength={2000}
            />
            <button className="btn btn-primary" onClick={handleSend} disabled={!draft.trim()}>
              {lang === 'es' ? 'Enviar' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
